import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { subscribeToDismissFloatingLayers } from "../_shared/dismissFloatingLayers"
import { cn } from "../../utils/cn"
import { DsAvatar } from "../DsAvatar"
import { DSIcon } from "../DSIcon"
import { resolveUserDropdownPanelStyle, type OverlayStyle } from "./dropdownUtils"

type DsUserDropdownProps = {
  avatarSrc?: string
  children: ReactNode
  className?: string
  "data-id"?: string
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
  open?: boolean
  userName: string
} & Omit<ComponentPropsWithoutRef<"div">, "children" | "className">

function DsUserDropdown({
  avatarSrc,
  children,
  className,
  "data-id": dataId = "user-dropdown",
  disabled = false,
  onOpenChange,
  open,
  userName,
  ...props
}: DsUserDropdownProps) {
  const generatedId = useId()
  const menuId = `${generatedId}-menu`
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<OverlayStyle | null>(null)

  const isControlled = open !== undefined
  const isOpen = isControlled ? open : uncontrolledOpen

  function setOpenState(nextOpen: boolean) {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  }

  function updatePanelStyle() {
    setPanelStyle(resolveUserDropdownPanelStyle(triggerRef.current))
  }

  function handleTriggerClick() {
    if (disabled) {
      return
    }

    setOpenState(!isOpen)
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    updatePanelStyle()
  }, [isOpen, userName])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return
      }

      setOpenState(false)
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenState(false)
      }
    }

    function handleResize() {
      setOpenState(false)
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("keydown", handleEscape)
    window.addEventListener("resize", handleResize)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("keydown", handleEscape)
      window.removeEventListener("resize", handleResize)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    return subscribeToDismissFloatingLayers(() => {
      setOpenState(false)
    })
  }, [isOpen])

  return (
    <div
      className={cn("relative inline-flex font-inter", className)}
      data-slot="user-dropdown-root"
      ref={rootRef}
      {...props}
    >
      <button
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={userName}
        className={cn(
          "inline-flex h-10 max-w-full items-center gap-2 rounded-[12px] transition-colors duration-200 ease-out",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          isOpen
            ? "bg-dark-light-mode-background px-2 py-1"
            : "bg-transparent py-0 pr-3 pl-0"
        )}
        data-id={dataId}
        data-slot="user-dropdown-trigger"
        data-state={isOpen ? "open" : "closed"}
        disabled={disabled}
        onClick={handleTriggerClick}
        ref={triggerRef}
        type="button"
      >
        <DsAvatar
          className={cn(
            "transition-colors duration-200 ease-out",
            isOpen
              ? "bg-brand-primary-3 text-body-light-mode-1"
              : "bg-dark-light-mode-background text-brand-primary-3"
          )}
          name={userName}
          src={avatarSrc}
        />
        <span
          className={cn(
            "max-w-[12rem] truncate text-[14px] leading-normal tracking-normal transition-colors duration-200 ease-out",
            isOpen ? "font-semibold text-brand-primary-3" : "font-normal text-dark-3"
          )}
          data-slot="user-dropdown-name"
        >
          {userName}
        </span>
        <DSIcon
          aria-hidden
          className={cn(
            "shrink-0 text-gray-5 transition-transform duration-200 ease-out",
            isOpen ? "rotate-180 text-brand-primary-3" : undefined
          )}
          name="ArrowDown2"
          size={24}
        />
      </button>

      {isOpen && panelStyle
        ? createPortal(
            <div
              className="fixed z-50 flex flex-col gap-4 rounded-3xl bg-body-light-mode-1 p-6 shadow-[0_24px_80px_oklch(0.2101_0.0318_264.66_/_0.18)] transition-[opacity,transform] duration-200 ease-out [&>*]:rounded-[12px] [&_button]:cursor-pointer [&_button]:transition-colors [&_button]:duration-150 [&_button]:ease-out [&_button:hover]:bg-gray-2"
              data-slot="user-dropdown-panel"
              id={menuId}
              ref={panelRef}
              role="menu"
              style={{
                left: panelStyle.left,
                top: panelStyle.top,
                width: panelStyle.width,
              }}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

export { DsUserDropdown }
export type { DsUserDropdownProps }
